import React from "react";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { TALK_DOWN_FLAG } from "../../common/TalkDown/useTalkDownEnabled";
import {
  AlertTalkDownLiveBanner,
  AlertTalkDownLiveStatus,
  AlertTalkDownMegaphone,
} from "./AlertTalkDownLiveControl";
import { useAlertTalkDownLibrary } from "./AlertTalkDownLibraryPicker";
import { AlertListenInControl } from "./AlertListenInControl";

export type AlertViewMode = "clip" | "live" | "split";
export type StreamProtocol = "whep" | "hls";

interface AlertViewModeTabsProps {
  viewMode: AlertViewMode;
  onViewModeChange: (mode: AlertViewMode) => void;
  streamProtocol: StreamProtocol;
  onStreamProtocolChange: (protocol: StreamProtocol) => void;
  showStreamProtocolSelector?: boolean;
  width?: number;
  /** Talk-down (showTalkDown): camera context for the megaphone control. */
  talkDownCameraId?: string | number | null;
  talkDownCameraName?: string;
  talkDownIntegrationTypeName?: string | null;
  talkDownCapable?: boolean | null;
  /** Parent group from the Alerts filter — preselects Record new message. */
  talkDownParentGroupId?: string | number | null;
  /** Site for default assignment when recording from Alerts. */
  talkDownSiteId?: string | number | null;
  talkDownSiteName?: string | null;
}

const TABS: { id: AlertViewMode; label: string }[] = [
  { id: "clip", label: "Alert Clip" },
  { id: "live", label: "Live Stream" },
  { id: "split", label: "Split View" },
];

export const AlertViewModeTabs: React.FC<AlertViewModeTabsProps> = ({
  viewMode,
  onViewModeChange,
  streamProtocol,
  onStreamProtocolChange,
  showStreamProtocolSelector = false,
  width = 540,
  talkDownCameraId,
  talkDownCameraName = "",
  talkDownIntegrationTypeName,
  talkDownCapable,
  talkDownParentGroupId,
  talkDownSiteId,
  talkDownSiteName,
}) => {
  const { flags } = useFeatureFlag();
  const showTalkDown = Boolean(flags?.[TALK_DOWN_FLAG]);
  const [liveStatus, setLiveStatus] =
    React.useState<AlertTalkDownLiveStatus | null>(null);
  const endSessionRef = React.useRef<(() => void) | null>(null);
  const library = useAlertTalkDownLibrary(
    talkDownCameraId,
    talkDownCameraName,
    talkDownParentGroupId,
    talkDownSiteId,
    talkDownSiteName
  );

  const handleLiveStatusChange = React.useCallback(
    (status: AlertTalkDownLiveStatus | null) => {
      setLiveStatus(status);
    },
    []
  );

  const handleRegisterEndSession = React.useCallback(
    (endSession: (() => void) | null) => {
      endSessionRef.current = endSession;
    },
    []
  );

  const handleDismissBanner = React.useCallback(() => {
    endSessionRef.current?.();
    setLiveStatus(null);
  }, []);

  return (
    <div style={{ width }}>
      <div className="mb-[10px] flex items-stretch gap-[2px] border-b border-actuate-grey bg-[#FAFAFB] font-mulish">
        <div className="flex items-end gap-[2px]" role="tablist">
          {TABS.map((tab) => {
            const isActive = viewMode === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onViewModeChange(tab.id)}
                className={`border-b-2 px-4 py-[10px] text-[12px] font-medium transition-colors ${
                  isActive
                    ? "border-actuate-orange text-actuate-blue"
                    : "border-transparent text-gray-500 hover:text-actuate-blue"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {showStreamProtocolSelector && viewMode !== "clip" && (
          <select
            value={streamProtocol}
            onChange={(e) =>
              onStreamProtocolChange(e.target.value as StreamProtocol)
            }
            aria-label="Live stream protocol"
            className={`self-center rounded border border-actuate-grey bg-white px-2 py-1 text-[12px] font-medium text-actuate-blue ${
              showTalkDown ? "ml-1 mr-auto" : "ml-auto mr-1"
            }`}
          >
            <option value="hls">LL-HLS (~2-3s)</option>
            <option value="whep">WHEP (&lt;1s)</option>
          </select>
        )}
        {showTalkDown && (
          <div
            className={`flex items-center justify-end gap-2.5 self-center py-1 ${
              showStreamProtocolSelector && viewMode !== "clip"
                ? ""
                : "ml-auto"
            }`}
            data-testid="talk-down-controls"
          >
            <AlertTalkDownMegaphone
              cameraId={talkDownCameraId}
              cameraName={talkDownCameraName}
              integrationTypeName={talkDownIntegrationTypeName}
              talkDownCapable={talkDownCapable}
              onStatusChange={handleLiveStatusChange}
              onRegisterEndSession={handleRegisterEndSession}
            />
            {library.button}
            <AlertListenInControl
              cameraId={talkDownCameraId}
              viewMode={viewMode}
            />
          </div>
        )}
      </div>
      {showTalkDown && liveStatus && (
        <AlertTalkDownLiveBanner
          status={liveStatus}
          onDismiss={handleDismissBanner}
        />
      )}
      {showTalkDown && !liveStatus && library.panel}
    </div>
  );
};
